import React, { createContext, useContext, useState, useEffect } from 'react'

import { DEFAULT_THEME } from 'config'
import PropTypes from 'prop-types'
import { useParams, useSearchParams } from 'react-router-dom'

import { BOOKING_FLEET_PATH } from '@/utils/routePaths'

import { useThemeControl } from '@/theme'

import { useGetServiceBookingFleet, useGetTheme } from '@/blocks/Booking/hooks'

export const AccountSettingsContext = createContext()
export const useAccountSettings = () => {
  const context = useContext(AccountSettingsContext) ?? {}
  return context
}
export const CUSTOM_COLORS_THEME_KEY = 'custom-colors-theme'
export const COLORS_THEME_PARAM = 'colorstheme'
export const CAN_USE_LETTERS_IN_REGISTRATION_NUMBER =
  'allowuselettersinregistrationnumber'
export const USE_INTERNATIONAL_PHONE_NUMBER = 'allowuseinternationalphonenumber'
export const USE_REFERENT_BOOKING = 'allowusereferentbooking'

const sessionCustomColorsTheme = sessionStorage.getItem(CUSTOM_COLORS_THEME_KEY)

export const AccountSettingsProvider = ({ children }) => {
  const { setColors } = useThemeControl()
  const { appPath } = useParams()
  const [searchParams] = useSearchParams()
  const [customColorsTheme, setCustomColorsTheme] = useState(
    sessionCustomColorsTheme,
  )
  const [
    canUseLettersInRegistrationNumber,
    setCanUseLettersInRegistrationNumber,
  ] = useState(false)
  const [canUseInternationalPhoneNumber, setCanUseInternationalPhoneNumber] =
    useState(false)
  const [canUseReferentBooking, setCanUseReferentBooking] = useState(false)
  const [fleetData, setFleetData] = useState(null)

  const {
    data: serviceBookingFleet,
    isSuccess: isServiceBookingFleetSuccess,
    isLoading,
  } = useGetServiceBookingFleet(appPath)

  const initFromFleetDataParameters = () => {
    if (fleetData) {
      setCanUseLettersInRegistrationNumber(
        getFleetParam(CAN_USE_LETTERS_IN_REGISTRATION_NUMBER) === 'yes',
      )
      setCanUseInternationalPhoneNumber(
        getFleetParam(USE_INTERNATIONAL_PHONE_NUMBER) === 'yes',
      )
      setCanUseReferentBooking(getFleetParam(USE_REFERENT_BOOKING) === 'yes')
      const colorsTheme = getFleetParam(COLORS_THEME_PARAM)
      if (colorsTheme) {
        setCustomColorsTheme(colorsTheme)
        sessionStorage.setItem(CUSTOM_COLORS_THEME_KEY, colorsTheme)
      } else {
        setCustomColorsTheme(null)
      }
    }
  }

  const initFromUrlQueryParameters = () => {
    const colorsTheme = searchParams?.get('theme')
    if (colorsTheme) {
      setCustomColorsTheme(colorsTheme)
      sessionStorage.setItem(CUSTOM_COLORS_THEME_KEY, colorsTheme)
    }
  }
  const initFromConfig = () => {
    if (DEFAULT_THEME && !customColorsTheme) {
      setCustomColorsTheme(DEFAULT_THEME)
    }
  }

  useEffect(initFromFleetDataParameters, [fleetData])
  useEffect(initFromUrlQueryParameters, [searchParams])
  useEffect(initFromConfig, [DEFAULT_THEME])

  const { data: themeData, isSuccess: isThemeSuccess } = useGetTheme(
    { themeId: customColorsTheme },
    {
      enabled: !!customColorsTheme,
    },
  )

  useEffect(() => {
    if (isServiceBookingFleetSuccess) {
      setFleetData(serviceBookingFleet)
    }
  }, [isServiceBookingFleetSuccess, serviceBookingFleet])

  useEffect(() => {
    if (isThemeSuccess && themeData) {
      setColors(themeData)
    }
  }, [isThemeSuccess, setColors, themeData])

  const getFleetParam = (param) => {
    return fleetData?.appParams ? fleetData.appParams[param] : null
  }

  const getFleetBookingPath = (queryParams) => {
    return `${BOOKING_FLEET_PATH.replace(':appPath', appPath)}${
      queryParams ? `?${queryParams}` : ''
    }`
  }

  const value = {
    appPath,
    fleetData,
    setFleetData,
    canUseLettersInRegistrationNumber,
    canUseInternationalPhoneNumber,
    canUseReferentBooking,
    isLoading,
    getFleetBookingPath,
    theme: customColorsTheme,
  }

  return (
    <AccountSettingsContext.Provider value={value}>
      {children}
    </AccountSettingsContext.Provider>
  )
}

AccountSettingsProvider.propTypes = {
  children: PropTypes.node,
}

export default AccountSettingsProvider
